import { useRef, useState } from "react";
import { CRMPlatform } from "@/enums/CRM.enum";
import { getPlatformName } from "@/utils/crm.util";
import { useQuery } from "@tanstack/react-query";

import useSalesyApiClient from "@/hooks/useSalesyApiClient";

export const useCRMConnectionStatusKey = ["crm-status-connection"];

const MAX_RETRIES = 5;

type Props = {
  platform?: CRMPlatform | null;
  refetch?: boolean;
};

export const useCRMConnectionsStatus = ({ platform, refetch }: Props) => {
  const { apiClient } = useSalesyApiClient();

  const [maxRetriesReached, setMaxRetriesReached] = useState(false);
  const retryCountRef = useRef<number>(0);

  const fetchStatus = () => {
    retryCountRef.current++;
    return apiClient.crmConnections.getConnections();
  };

  const query = useQuery({
    queryKey: useCRMConnectionStatusKey,
    queryFn: fetchStatus,
    enabled: !maxRetriesReached,
    refetchInterval: (data) => {
      if (!refetch || data?.state.data?.hubspot.connected) {
        return false;
      }

      if (platform) {
        const platformName = getPlatformName(platform);

        // Check if the specific platform is connected, otherwise continue polling
        if (platformName && data?.state.data?.[platformName].connected) {
          return false;
        }
      }

      // If max retries reached, stop polling and set the flag
      if (retryCountRef.current >= MAX_RETRIES) {
        setMaxRetriesReached(true);
      }

      return 4000;
    },
    staleTime: 0,
  });

  return { ...query, maxRetriesReached };
};
